import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '../lib/supabase.js';

// Security constants
const SECURITY_CONFIG = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
  MAX_FILES_PER_UPLOAD: 20,
  MAX_FILENAME_LENGTH: 100,
  UPLOAD_COOLDOWN_MS: 1000, // Minimum time between uploads
  MAX_UPLOADS_PER_MINUTE: 30,
};

// Security utility functions
const SecurityUtils = {
  // Validate file type by checking magic bytes (file signature)
  validateFileMagicBytes: async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = (e) => {
        const arr = new Uint8Array(e.target.result).subarray(0, 12);
        let header = '';
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16).padStart(2, '0');
        }
        
        // Check magic bytes for common image formats
        const isJPEG = header.startsWith('ffd8ff');
        const isPNG = header.startsWith('89504e47');
        const isGIF = header.startsWith('474946');
        const isWebP = header.slice(0, 8) === '52494646' && header.slice(16, 24) === '57454250';
        
        resolve(isJPEG || isPNG || isGIF || isWebP);
      };
      reader.onerror = () => resolve(false);
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  },

  // Sanitize filename to prevent path traversal and injection
  sanitizeFilename: (filename) => {
    // Remove path components
    let sanitized = filename.split(/[/\\]/).pop();
    // Remove dangerous characters
    sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '_');
    // Prevent double extensions
    const parts = sanitized.split('.');
    if (parts.length > 2) {
      sanitized = parts[0] + '.' + parts[parts.length - 1];
    }
    // Limit length
    if (sanitized.length > SECURITY_CONFIG.MAX_FILENAME_LENGTH) {
      const ext = sanitized.split('.').pop();
      sanitized = sanitized.substring(0, SECURITY_CONFIG.MAX_FILENAME_LENGTH - ext.length - 1) + '.' + ext;
    }
    return sanitized;
  },

  // Validate work name
  sanitizeWorkName: (name) => {
    // Remove potentially dangerous characters, allow Hungarian characters
    return name
      .replace(/[<>:"/\\|?*]/g, '')
      .replace(/\.\./g, '')
      .trim()
      .substring(0, 50);
  },

  // Check file extension
  hasValidExtension: (filename) => {
    const ext = '.' + filename.split('.').pop().toLowerCase();
    return SECURITY_CONFIG.ALLOWED_EXTENSIONS.includes(ext);
  },

  // Generate secure random string for filenames
  generateSecureId: () => {
    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    return Array.from(array, n => n.toString(36)).join('');
  }
};

export default function AdminDashboard() {
  const [workName, setWorkName] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);
  const [editingWork, setEditingWork] = useState(null);
  const [newWorkName, setNewWorkName] = useState('');
  const [selectedWorkForImages, setSelectedWorkForImages] = useState(null);
  const [addingImagesTo, setAddingImagesTo] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [additionalPreviews, setAdditionalPreviews] = useState([]);
  const [toast, setToast] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]); // Rate limiting
  const [lastUploadTime, setLastUploadTime] = useState(0);
  const navigate = useNavigate();

  // Toast notification helper
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const isLoggedIn = sessionStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin');
    }
  }, [navigate]);

  useEffect(() => {
    loadWorks();
  }, []);

  const loadWorks = async () => {
    // Ha nincs valódi Supabase konfiguráció, ne próbáljunk betölteni
    if (!isSupabaseConfigured) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .storage
        .from('works')
        .list('', { limit: 100, sortBy: { column: 'created_at', order: 'desc' } });

      if (error) throw error;

      const groupedWorks = {};
      data.forEach(file => {
        // Updated regex to match both old timestamp format and new secure ID format
        const match = file.name.match(/^(.+?)_[a-z0-9]+_\d+\./i);
        if (match) {
          const workName = match[1];
          if (!groupedWorks[workName]) {
            groupedWorks[workName] = {
              files: [],
              created: file.created_at
            };
          }
          
          const { data: urlData } = supabase.storage
            .from('works')
            .getPublicUrl(file.name);
          
          groupedWorks[workName].files.push({
            name: file.name,
            url: urlData.publicUrl,
            created: file.created_at
          });
        }
      });

      setWorks(Object.entries(groupedWorks).map(([name, data]) => ({
        name,
        files: data.files,
        count: data.files.length,
        created: data.created
      })));
    } catch (error) {
      console.error('Hiba a munkák betöltésekor:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = useCallback(async (selectedFiles) => {
    const validatedFiles = [];
    const errors = [];

    // Limit number of files
    const filesToProcess = selectedFiles.slice(0, SECURITY_CONFIG.MAX_FILES_PER_UPLOAD);
    if (selectedFiles.length > SECURITY_CONFIG.MAX_FILES_PER_UPLOAD) {
      errors.push(`Maximum ${SECURITY_CONFIG.MAX_FILES_PER_UPLOAD} fájl tölthető fel egyszerre.`);
    }

    for (const file of filesToProcess) {
      // Check MIME type
      if (!SECURITY_CONFIG.ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}": Nem engedélyezett fájltípus.`);
        continue;
      }

      // Check extension
      if (!SecurityUtils.hasValidExtension(file.name)) {
        errors.push(`"${file.name}": Nem engedélyezett kiterjesztés.`);
        continue;
      }

      // Check file size
      if (file.size > SECURITY_CONFIG.MAX_FILE_SIZE) {
        errors.push(`"${file.name}": Túl nagy (max ${SECURITY_CONFIG.MAX_FILE_SIZE / 1024 / 1024}MB).`);
        continue;
      }

      // Validate magic bytes (real file type check)
      const isValidImage = await SecurityUtils.validateFileMagicBytes(file);
      if (!isValidImage) {
        errors.push(`"${file.name}": Érvénytelen képfájl (sérült vagy hamis kiterjesztés).`);
        continue;
      }

      validatedFiles.push(file);
    }

    // Show errors if any
    if (errors.length > 0) {
      showToast(`⚠️ ${errors.length} fájl elutasítva: ${errors[0]}`, 'error');
      console.warn('File validation errors:', errors);
    }

    setFiles(validatedFiles);
    const previews = validatedFiles.map(file => URL.createObjectURL(file));
    setPreviewImages(previews);
  }, []);

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previewImages.filter((_, i) => i !== index);
    
    URL.revokeObjectURL(previewImages[index]);
    
    setFiles(newFiles);
    setPreviewImages(newPreviews);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!isSupabaseConfigured) {
      showToast('⚠️ Supabase nincs konfigurálva! Lásd a figyelmeztetést fent.', 'error');
      return;
    }
    
    if (!workName || files.length === 0) {
      showToast('⚠️ Kérem adja meg a munka nevét és válasszon képeket!', 'error');
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastUploadTime < SECURITY_CONFIG.UPLOAD_COOLDOWN_MS) {
      showToast('⚠️ Túl gyors feltöltés! Várjon egy pillanatot.', 'error');
      return;
    }

    // Check uploads per minute
    const recentUploads = uploadHistory.filter(t => now - t < 60000);
    if (recentUploads.length >= SECURITY_CONFIG.MAX_UPLOADS_PER_MINUTE) {
      showToast('⚠️ Túl sok feltöltés! Várjon egy percet.', 'error');
      return;
    }

    // Sanitize work name
    const sanitizedWorkName = SecurityUtils.sanitizeWorkName(workName);
    if (!sanitizedWorkName) {
      showToast('⚠️ Érvénytelen munka név!', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Re-validate file before upload (defense in depth)
        const isValidImage = await SecurityUtils.validateFileMagicBytes(file);
        if (!isValidImage) {
          throw new Error(`Érvénytelen képfájl: ${file.name}`);
        }

        const fileExt = file.name.split('.').pop().toLowerCase();
        // Use secure random ID instead of predictable timestamp
        const secureId = SecurityUtils.generateSecureId();
        const fileName = `${sanitizedWorkName}_${secureId}_${i}.${fileExt}`;

        const { error } = await supabase.storage
          .from('works')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type // Explicitly set content type
          });

        if (error) throw error;
        
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      // Update rate limiting
      setLastUploadTime(now);
      setUploadHistory(prev => [...prev.filter(t => now - t < 60000), now]);

      showToast('✅ Képek sikeresen feltöltve!');
      setWorkName('');
      setFiles([]);
      setPreviewImages([]);
      setUploadProgress(0);
      loadWorks();
    } catch (error) {
      console.error('Feltöltési hiba:', error);
      showToast('❌ Hiba történt a feltöltés során: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (workName) => {
    if (!window.confirm(`Biztos törli a "${workName}" munkát és az összes képét?`)) return;

    try {
      const work = works.find(w => w.name === workName);
      const fileNames = work.files.map(f => f.name);

      const { error } = await supabase.storage
        .from('works')
        .remove(fileNames);

      if (error) throw error;

      showToast('✅ Munka törölve!');
      loadWorks();
    } catch (error) {
      console.error('Törlési hiba:', error);
      showToast('❌ Hiba történt a törlés során: ' + error.message, 'error');
    }
  };

  const handleDeleteImage = async (work, imageFileName) => {
    if (!window.confirm('Biztos törli ezt a képet?')) return;

    try {
      const { error } = await supabase.storage
        .from('works')
        .remove([imageFileName]);

      if (error) throw error;

      showToast('✅ Kép törölve!');
      
      // Ha ez volt az utolsó kép, zárjuk be a modalt
      if (work.files.length === 1) {
        setSelectedWorkForImages(null);
      }
      
      loadWorks();
    } catch (error) {
      console.error('Törlési hiba:', error);
      showToast('❌ Hiba történt a törlés során: ' + error.message, 'error');
    }
  };

  const startRename = (work) => {
    setEditingWork(work);
    setNewWorkName(work.name);
  };

  const cancelRename = () => {
    setEditingWork(null);
    setNewWorkName('');
  };

  const handleRename = async () => {
    if (!newWorkName || newWorkName === editingWork.name) {
      cancelRename();
      return;
    }

    // Sanitize new work name
    const sanitizedNewName = SecurityUtils.sanitizeWorkName(newWorkName);
    if (!sanitizedNewName) {
      showToast('⚠️ Érvénytelen munka név!', 'error');
      return;
    }

    try {
      // Minden fájlt át kell nevezni
      for (const file of editingWork.files) {
        const fileExt = file.name.split('.').pop().toLowerCase();
        // Extract ID part (works with both old timestamp and new secure ID format)
        const idMatch = file.name.match(/_([a-z0-9]+)_(\d+)\./i);
        const id = idMatch ? idMatch[1] : SecurityUtils.generateSecureId();
        const index = idMatch ? idMatch[2] : 0;
        const newFileName = `${sanitizedNewName}_${id}_${index}.${fileExt}`;

        // Letöltjük a fájlt
        const { data: fileData, error: downloadError } = await supabase.storage
          .from('works')
          .download(file.name);

        if (downloadError) throw downloadError;

        // Feltöltjük új néven
        const { error: uploadError } = await supabase.storage
          .from('works')
          .upload(newFileName, fileData, {
            cacheControl: '3600',
            upsert: false,
            contentType: `image/${fileExt === 'jpg' ? 'jpeg' : fileExt}`
          });

        if (uploadError) throw uploadError;

        // Töröljük a régit
        const { error: deleteError } = await supabase.storage
          .from('works')
          .remove([file.name]);

        if (deleteError) throw deleteError;
      }

      showToast('✅ Munka átnevezve!');
      cancelRename();
      loadWorks();
    } catch (error) {
      console.error('Átnevezési hiba:', error);
      showToast('❌ Hiba történt az átnevezés során: ' + error.message, 'error');
    }
  };

  const handleAddImages = async () => {
    if (!addingImagesTo || additionalFiles.length === 0) {
      showToast('⚠️ Válasszon képeket!', 'error');
      return;
    }

    // Rate limiting check
    const now = Date.now();
    if (now - lastUploadTime < SECURITY_CONFIG.UPLOAD_COOLDOWN_MS) {
      showToast('⚠️ Túl gyors feltöltés! Várjon egy pillanatot.', 'error');
      return;
    }

    const recentUploads = uploadHistory.filter(t => now - t < 60000);
    if (recentUploads.length >= SECURITY_CONFIG.MAX_UPLOADS_PER_MINUTE) {
      showToast('⚠️ Túl sok feltöltés! Várjon egy percet.', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < additionalFiles.length; i++) {
        const file = additionalFiles[i];
        
        // Re-validate before upload
        const isValidImage = await SecurityUtils.validateFileMagicBytes(file);
        if (!isValidImage) {
          throw new Error(`Érvénytelen képfájl: ${file.name}`);
        }

        const fileExt = file.name.split('.').pop().toLowerCase();
        const secureId = SecurityUtils.generateSecureId();
        const fileName = `${addingImagesTo.name}_${secureId}_${i}.${fileExt}`;

        const { error } = await supabase.storage
          .from('works')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false,
            contentType: file.type
          });

        if (error) throw error;
        
        setUploadProgress(Math.round(((i + 1) / additionalFiles.length) * 100));
      }

      // Update rate limiting
      setLastUploadTime(now);
      setUploadHistory(prev => [...prev.filter(t => now - t < 60000), now]);

      showToast('✅ Képek sikeresen hozzáadva!');
      setAddingImagesTo(null);
      setAdditionalFiles([]);
      setAdditionalPreviews([]);
      setUploadProgress(0);
      loadWorks();
    } catch (error) {
      console.error('Feltöltési hiba:', error);
      showToast('❌ Hiba történt a feltöltés során: ' + error.message, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalFileChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = Array.from(e.target.files);
      const validatedFiles = [];
      
      for (const file of selectedFiles.slice(0, SECURITY_CONFIG.MAX_FILES_PER_UPLOAD)) {
        // Validate MIME type
        if (!SECURITY_CONFIG.ALLOWED_TYPES.includes(file.type)) continue;
        // Validate extension
        if (!SecurityUtils.hasValidExtension(file.name)) continue;
        // Validate size
        if (file.size > SECURITY_CONFIG.MAX_FILE_SIZE) continue;
        // Validate magic bytes
        const isValid = await SecurityUtils.validateFileMagicBytes(file);
        if (!isValid) continue;
        
        validatedFiles.push(file);
      }

      if (validatedFiles.length < selectedFiles.length) {
        showToast(`⚠️ ${selectedFiles.length - validatedFiles.length} fájl elutasítva biztonsági okokból.`, 'error');
      }

      setAdditionalFiles(validatedFiles);
      const previews = validatedFiles.map(file => URL.createObjectURL(file));
      setAdditionalPreviews(previews);
    }
  };

  const removeAdditionalFile = (index) => {
    const newFiles = additionalFiles.filter((_, i) => i !== index);
    const newPreviews = additionalPreviews.filter((_, i) => i !== index);
    
    URL.revokeObjectURL(additionalPreviews[index]);
    
    setAdditionalFiles(newFiles);
    setAdditionalPreviews(newPreviews);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_logged_in');
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 z-[9999] animate-slide-in">
          <div className={`px-4 md:px-6 py-3 md:py-4 rounded-lg shadow-2xl flex items-center gap-2 md:gap-3 md:min-w-[300px] ${
            toast.type === 'error' 
              ? 'bg-red-600 text-white' 
              : 'bg-green-600 text-white'
          }`}>
            <span className="text-sm md:text-lg font-medium flex-1">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-auto hover:bg-white/20 rounded-full p-1 transition-colors flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1 text-sm md:text-base">Munkáink kezelése</p>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1 md:gap-2 px-3 md:px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors text-sm md:text-base"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="hidden sm:inline">Kijelentkezés</span>
              <span className="sm:hidden">Kilépés</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Supabase Configuration Warning */}
        {!isSupabaseConfigured && (
          <div className="bg-amber-100 border-l-4 border-amber-500 text-amber-900 p-6 mb-8 rounded-lg">
            <div className="flex items-start gap-4">
              <svg className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-bold text-lg mb-2">⚠️ Supabase nincs konfigurálva</h3>
                <p className="mb-3">
                  Az admin funkciók használatához be kell állítanod egy valódi Supabase projektet.
                </p>
                <div className="bg-white/50 rounded-lg p-4 text-sm space-y-2">
                  <p className="font-semibold">Lépések:</p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Menj a <a href="https://supabase.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://supabase.com</a> oldalra</li>
                    <li>Hozz létre egy új projektet</li>
                    <li>Hozz létre egy "works" nevű Storage Bucket-et (Settings → Storage)</li>
                    <li>Másold ki a Project URL-t és az anon public API kulcsot</li>
                    <li>Írd be őket a <code className="bg-gray-200 px-2 py-1 rounded">.env</code> fájlba</li>
                    <li>Indítsd újra a development szervert</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Upload Section */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8 mb-6 md:mb-8">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div className="bg-blue-100 p-2 md:p-3 rounded-lg md:rounded-xl">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">Új munka feltöltése</h2>
              <p className="text-gray-600 text-sm md:text-base">Adja meg a munka nevét és töltse fel a képeket</p>
            </div>
          </div>

          <form onSubmit={handleUpload} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Munka neve *
              </label>
              <input
                type="text"
                value={workName}
                onChange={(e) => setWorkName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="pl: Konyhabútor modern design"
                required
              />
            </div>

            <div
              className={`relative border-2 border-dashed rounded-xl p-4 md:p-8 text-center transition-all ${
                dragActive 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                id="fileInput"
                type="file"
                onChange={handleFileChange}
                multiple
                accept="image/*"
                className="hidden"
              />
              
              <div className="space-y-3 md:space-y-4">
                <div className="flex justify-center">
                  <div className="bg-blue-100 p-3 md:p-4 rounded-full">
                    <svg className="w-8 h-8 md:w-12 md:h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                </div>
                
                <div>
                  <p className="text-base md:text-lg font-semibold text-gray-700 mb-1 md:mb-2">
                    <span className="hidden md:inline">Húzza ide a képeket vagy kattintson a tallózáshoz</span>
                    <span className="md:hidden">Kattintson a képek kiválasztásához</span>
                  </p>
                  <p className="text-xs md:text-sm text-gray-500">
                    PNG, JPG, GIF • Max 10MB / kép
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => document.getElementById('fileInput').click()}
                  className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-sm md:text-base"
                >
                  <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  Képek tallózása
                </button>
              </div>
            </div>

            {previewImages.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Kiválasztott képek ({files.length})
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {previewImages.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="absolute bottom-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploading && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-700">Feltöltés folyamatban...</span>
                  <span className="text-blue-600 font-bold">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-blue-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={uploading || files.length === 0 || !isSupabaseConfigured}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2 shadow-lg"
            >
              {uploading ? (
                <>
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Feltöltés... {uploadProgress}%</span>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Feltöltés ({files.length} kép)</span>
                </>
              )}
            </button>
          </form>
        </div>

        {/* Works List */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-4 md:p-8">
          <div className="flex items-center gap-3 mb-4 md:mb-6">
            <div className="bg-green-100 p-2 md:p-3 rounded-lg md:rounded-xl">
              <svg className="w-5 h-5 md:w-6 md:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-gray-900">Feltöltött munkák</h2>
              <p className="text-gray-600 text-sm md:text-base">Összesen {works.length} munka</p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600"></div>
              <p className="text-gray-600 mt-4">Betöltés...</p>
            </div>
          ) : works.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-600 text-lg">Még nincsenek feltöltött munkák.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {works.map((work, index) => (
                <div
                  key={index}
                  className="p-3 md:p-4 border border-gray-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-2 md:p-3 rounded-lg flex-shrink-0">
                      <svg className="w-5 h-5 md:w-6 md:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingWork?.name === work.name ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={newWorkName}
                            onChange={(e) => setNewWorkName(e.target.value)}
                            className="flex-1 min-w-[150px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Új név"
                            autoFocus
                          />
                          <button
                            onClick={handleRename}
                            className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                          >
                            ✓
                          </button>
                          <button
                            onClick={cancelRename}
                            className="px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <>
                          <h3 className="font-bold text-gray-900 text-base md:text-lg truncate">{work.name}</h3>
                          <p className="text-xs md:text-sm text-gray-600">{work.count} kép</p>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Mobile-friendly action buttons - always visible on mobile */}
                  <div className="flex items-center gap-2 mt-3 md:mt-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity flex-wrap md:flex-nowrap md:absolute md:right-4 md:top-1/2 md:-translate-y-1/2">
                    <button
                      onClick={() => setSelectedWorkForImages(work)}
                      className="flex items-center gap-1 px-2 md:px-3 py-1.5 md:py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors text-xs md:text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                      <span className="hidden sm:inline">Képek</span>
                    </button>
                    <button
                      onClick={() => setAddingImagesTo(work)}
                      className="flex items-center gap-1 px-2 md:px-3 py-1.5 md:py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors text-xs md:text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span className="hidden sm:inline">Hozzáad</span>
                    </button>
                    <button
                      onClick={() => startRename(work)}
                      className="flex items-center gap-1 px-2 md:px-3 py-1.5 md:py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors text-xs md:text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      <span className="hidden sm:inline">Átnevez</span>
                    </button>
                    <button
                      onClick={() => handleDelete(work.name)}
                      className="flex items-center gap-1 px-2 md:px-3 py-1.5 md:py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors text-xs md:text-sm"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span className="hidden sm:inline">Törlés</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Images Modal */}
      {addingImagesTo && (
        <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
          <div className="min-h-screen p-2 md:p-4 flex items-center justify-center">
            <div className="max-w-2xl w-full bg-white rounded-xl md:rounded-2xl p-4 md:p-8">
              {/* Header */}
              <div className="flex justify-between items-center mb-4 md:mb-6">
                <div>
                  <h3 className="text-lg md:text-2xl font-bold text-gray-900">Képek hozzáadása</h3>
                  <p className="text-gray-600 mt-1 text-sm md:text-base truncate max-w-[200px] md:max-w-none">{addingImagesTo.name}</p>
                </div>
                <button
                  onClick={() => {
                    setAddingImagesTo(null);
                    setAdditionalFiles([]);
                    setAdditionalPreviews([]);
                  }}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-full p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* File Input */}
              <div className="mb-6">
                <input
                  id="additionalFileInput"
                  type="file"
                  onChange={handleAdditionalFileChange}
                  multiple
                  accept="image/*"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('additionalFileInput').click()}
                  className="w-full border-2 border-dashed border-gray-300 rounded-xl p-8 hover:border-blue-400 hover:bg-blue-50 transition-all"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-blue-100 p-4 rounded-full">
                      <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </div>
                    <p className="font-semibold text-gray-700">Képek kiválasztása</p>
                    <p className="text-sm text-gray-500">Kattintson a tallózáshoz</p>
                  </div>
                </button>
              </div>

              {/* Preview Images */}
              {additionalPreviews.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Kiválasztott képek ({additionalFiles.length})
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    {additionalPreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeAdditionalFile(index)}
                          className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {uploading && (
                <div className="mb-6 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700">Feltöltés folyamatban...</span>
                    <span className="text-blue-600 font-bold">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-blue-600 h-full rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setAddingImagesTo(null);
                    setAdditionalFiles([]);
                    setAdditionalPreviews([]);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
                >
                  Mégse
                </button>
                <button
                  onClick={handleAddImages}
                  disabled={uploading || additionalFiles.length === 0}
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Feltöltés... {uploadProgress}%
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Hozzáadás ({additionalFiles.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {selectedWorkForImages && (
        <div className="fixed inset-0 bg-black/90 z-50 overflow-y-auto">
          <div className="min-h-screen p-4">
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="sticky top-0 bg-white/10 backdrop-blur-md rounded-xl p-4 mb-6 flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">{selectedWorkForImages.name}</h3>
                  <p className="text-gray-300">{selectedWorkForImages.count} kép</p>
                </div>
                <button
                  onClick={() => setSelectedWorkForImages(null)}
                  className="bg-white/10 hover:bg-white/20 text-white rounded-full p-3 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Images Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {selectedWorkForImages.files.map((file, idx) => (
                  <div key={idx} className="relative group">
                    <img
                      src={file.url}
                      alt={`${selectedWorkForImages.name} ${idx + 1}`}
                      className="w-full h-64 object-cover rounded-xl"
                    />
                    <button
                      onClick={() => handleDeleteImage(selectedWorkForImages, file.name)}
                      className="absolute top-3 right-3 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <div className="absolute bottom-3 left-3 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                      {idx + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
